import { copiedAsFiles } from '@cp/shared';
import { can } from '@/utils/permissions';
import type { SiteDetail } from '@/types/api';

/**
 * What can be done to a site right now, and by this account: the rules behind
 * every button on a site's page, in one place.
 *
 * Operations need a running site: a failed creation has nothing to restart or
 * copy, and one still provisioning is busy with its own job. As with `can`,
 * this only hides what cannot work; the server checks again.
 */
export function siteCapabilities(site: SiteDetail, roles: readonly string[]) {
  const running = site.status === 'ready' || site.status === 'suspended';
  const ready = site.status === 'ready';
  const wordpress = site.engine === 'wordpress';

  return {
    /** A site the panel watches but does not run (S7): only importing it or letting it go. */
    observed: site.engine === 'external',
    importObserved: can(roles, 'sites.create'),
    /** Import brings a WordPress under management; a ConvertStudio or static site has nothing to import. */
    importWordpress: can(roles, 'sites.create') && (site.platform === 'wordpress' || site.platform === null),

    deploy: running && can(roles, 'sites.deploy'),
    // A watched site is copied by the agent on its own server, once that server is connected (ADR-0033).
    backup: running && can(roles, 'backups.create') && (site.engine !== 'external' || site.hostNodeId !== null),
    // Production needs the stronger permission; the server checks the same rule
    // when the request arrives and again when the job runs.
    restore: running && can(roles, site.tier === 'production' ? 'backups.restore_production' : 'backups.restore'),
    migrate: ready && wordpress && site.nodeId !== null && can(roles, 'nodes.manage'),
    // A site the panel runs, or a site of a connected external server whose files are the whole site (ADR-0038).
    clone:
      ready &&
      (wordpress || (site.engine === 'external' && site.hostNodeId !== null && copiedAsFiles(site.platform))) &&
      can(roles, 'sites.create') &&
      can(roles, 'backups.create'),
    upgrade: ready && wordpress && can(roles, 'sites.deploy') && can(roles, 'backups.create'),
    editDomains: ready && wordpress && can(roles, 'sites.deploy'),
    destroy: site.status !== 'provisioning' && site.status !== 'destroying' && can(roles, 'sites.destroy'),
    // Only when the site's server has gone quiet: a server that answers is left the ordinary way.
    relocate: site.nodeStale && wordpress && running && can(roles, 'nodes.manage'),

    tag: can(roles, 'tags.manage'),
    /** What it runs on and where its admin is (ADR-0031). */
    editPlatform: can(roles, 'sites.deploy'),
    /** Credentials: the owner and administrators only. */
    credentials: can(roles, 'secrets.manage'),
    /** DNS records and Cloudflare settings through 2d2s (ADR-0032); the domain must also carry a write tag. */
    manageDns: can(roles, 'domains.manage'),
    manageImport: can(roles, 'sites.create') && can(roles, 'sites.deploy'),
    /** Template builds (ADR-0047): made from a running site of the panel that is not itself a site of one. */
    captureTemplate: ready && wordpress && site.templateBuild === null && can(roles, 'templates.manage'),
    /** The words of the template structure, in the site's language. */
    translate: ready && site.templateBuild !== null && can(roles, 'sites.deploy'),
    /** A new GEO opened to search engines. */
    launch: ready && site.launchPending && can(roles, 'sites.deploy'),
    removeRemnants: can(roles, 'nodes.manage'),
    revealCredential: ready && can(roles, 'sites.create'),
  };
}

export type SiteCapabilities = ReturnType<typeof siteCapabilities>;
